// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useExerciseSubmission } from '@hooks/mutations/ledger/useSubmission';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import {
  MintRequest,
  MintRequest_Accept,
} from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Model/Mint';
import { ContractId } from '@daml/types';
import { toDamlDisclosedContract } from '../../../../utils/common';
import { ERR_ACC_MINT, SUC_ACC_MINT } from '../../../../utils/strings';
import { useAcceptMintRequestContext } from './context/useAcceptMintRequestContext';

interface Args {
  mintRequestId: ContractId<MintRequest>;
}

export const useAcceptMintRequest2Step = () => {
  const getAcceptRequestMintContext = useAcceptMintRequestContext();
  const { submit: acceptMintRequest } = useExerciseSubmission(
    MintRequest.MintRequest_Accept,
    SUC_ACC_MINT,
    ERR_ACC_MINT,
  );

  return async (args: Args) => {
    const { mintRequestId } = args;

    const acceptRequestMintContext = await getAcceptRequestMintContext.mutateAsync({
      mintRequestId,
    });

    const payload: MintRequest_Accept = {
      extraArgs: {
        context: acceptRequestMintContext.choiceContextData as ChoiceContext,
        meta: { values: {} },
      },
    };

    const disclosedContracts =
      acceptRequestMintContext.disclosedContracts.map(toDamlDisclosedContract) || [];

    await acceptMintRequest({
      cid: mintRequestId,
      argument: payload,
      meta: {
        disclosedContracts,
      },
    });
  };
};
